import { banks, upiApps, creditCards } from '../data/mockData';
import { charges as chargesData } from '../data/charges';
import { BankIndex, UpiAppIndex, CreditCardIndex, BankDetail, UpiAppDetail, CreditCardDetail, ComparisonItem, Charge, ApplicableEntity, SearchSuggestion } from '../types';

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Create a unified map of all entities for easy lookup. This map is NOT country-filtered.
const allEntities = new Map<string, BankIndex | UpiAppIndex | CreditCardIndex>();
// Fix: Explicitly type the parameters to resolve type inference issues.
Object.values(banks).forEach((b: BankDetail) => allEntities.set(b.id, { id: b.id, slug: b.slug, name: b.name, category: b.category, country: b.country }));
// Fix: Explicitly type the parameters to resolve type inference issues.
Object.values(upiApps).forEach((a: UpiAppDetail) => allEntities.set(a.id, { id: a.id, slug: a.slug, name: a.name, country: a.country }));
// Fix: Explicitly type the parameters to resolve type inference issues.
Object.values(creditCards).forEach((c: CreditCardDetail) => allEntities.set(c.id, { id: c.id, slug: c.slug, name: c.name, country: c.country }));


// --- New Charge-centric Functions ---
export const getCharges = async (countryCode: string): Promise<Charge[]> => {
    await delay(100);
    return chargesData.filter(c => c.country === countryCode);
};

export const getChargeBySlug = async (slug: string): Promise<Charge | undefined> => {
    await delay(100);
    // Slug is globally unique, no country filter needed here
    return chargesData.find(c => c.id === slug);
};

export const resolveApplicableEntities = (charge: Charge): ApplicableEntity[] => {
    // This function resolves entities based on a charge, which is already country-specific.
    // The global allEntities map is used for lookup.
    return charge.applicableSources
        .map(source => {
            const entity = allEntities.get(source.sourceId);
            if (!entity) return null;
            return {
                ...entity,
                chargeDetails: {
                    amount: source.amount ?? charge.typicalAmount,
                    description: source.description ?? '',
                }
            };
        })
        .filter((e): e is ApplicableEntity => e !== null);
};

// --- Restored functions for entity-specific pages ---
export const getBankIndex = async (countryCode: string): Promise<BankIndex[]> => {
    await delay(100);
    return Object.values(banks)
        .filter(b => b.country === countryCode)
        .map(({ id, slug, name, category, country }) => ({ id, slug, name, category, country }));
};

export const getBankDetail = async (slug: string): Promise<BankDetail | undefined> => {
    await delay(100);
    // Slug is globally unique
    return banks[slug];
};

export const getUpiAppIndex = async (countryCode: string): Promise<UpiAppIndex[]> => {
    await delay(100);
    return Object.values(upiApps)
      .filter(a => a.country === countryCode)
      .map(({ id, slug, name, country }) => ({ id, slug, name, country }));
};

export const getUpiAppDetail = async (slug: string): Promise<UpiAppDetail | undefined> => {
    await delay(100);
    // Slug is globally unique
    return upiApps[slug];
};

export const getCreditCardIndex = async (countryCode: string): Promise<CreditCardIndex[]> => {
    await delay(100);
    return Object.values(creditCards)
      .filter(c => c.country === countryCode)
      .map(({ id, slug, name, country }) => ({ id, slug, name, country }));
};

export const getCreditCardDetail = async (slug: string): Promise<CreditCardDetail | undefined> => {
    await delay(100);
    // Slug is globally unique
    return creditCards[slug];
};

// --- New functions to get detailed charges for specific entities ---
export const getChargesForBank = async (bankId: string): Promise<Charge[]> => {
    await delay(50);
    return chargesData.filter(charge => 
        charge.applicableSources.some(source => source.sourceId === bankId)
    );
};

export const getChargesForUpiApp = async (appId: string): Promise<Charge[]> => {
    await delay(50);
    return chargesData.filter(charge => 
        charge.applicableSources.some(source => source.sourceId === appId)
    );
};

export const getChargesForCreditCard = async (cardId: string): Promise<Charge[]> => {
    await delay(50);
    return chargesData.filter(charge => 
        charge.applicableSources.some(source => source.sourceId === cardId)
    );
};

// --- Function for Comparison Page ---
export const getAllComparisonItems = async (countryCode: string): Promise<ComparisonItem[]> => {
    await delay(200);
    const allItems: ComparisonItem[] = [];

    // 1. Get charges from the main charges list
    const countryCharges = chargesData.filter(c => c.country === countryCode);
    countryCharges.forEach(charge => {
        const entities = resolveApplicableEntities(charge);
        entities.forEach(entity => {
            allItems.push({
                id: `${charge.id}-${entity.id}`, // e.g., 'nfs-chg-sbi'
                sourceName: entity.name,
                type: charge.fullName,
                amount: entity.chargeDetails.amount,
                description: entity.chargeDetails.description || `Charge applied by ${entity.name}.`,
            });
        });
    });

    // 2. Get charges from individual banks
    Object.values(banks)
        // Fix: Explicitly type the parameter to resolve type inference issues.
        .filter((b: BankDetail) => b.country === countryCode)
        .forEach((bank: BankDetail) => {
            bank.charges.forEach(charge => {
                allItems.push({
                    id: charge.id, // e.g., 'sbi-min-bal'
                    sourceName: bank.name,
                    type: charge.type,
                    amount: charge.amount,
                    description: charge.description,
                });
            });
        });

    // 3. Get charges from UPI apps
    Object.values(upiApps)
        // Fix: Explicitly type the parameter to resolve type inference issues.
        .filter((a: UpiAppDetail) => a.country === countryCode)
        .forEach((app: UpiAppDetail) => {
            app.charges.forEach(charge => {
                allItems.push({
                    id: charge.id, // e.g., 'phonepe-recharge'
                    sourceName: app.name,
                    type: charge.type,
                    amount: charge.amount,
                    description: charge.description,
                });
            });
        });

    // 4. Get fees from credit cards
    Object.values(creditCards)
        // Fix: Explicitly type the parameter to resolve type inference issues.
        .filter((c: CreditCardDetail) => c.country === countryCode)
        .forEach((card: CreditCardDetail) => {
            card.charges.forEach(charge => {
                allItems.push({
                    id: charge.id, // e.g., 'regalia-annual-fee'
                    sourceName: card.name,
                    type: charge.type,
                    amount: charge.amount,
                    description: charge.description,
                });
            });
        });

    return allItems;
};


// --- Function for Add Charge Page ---
export const getAllEntityIdsAndNames = async (countryCode: string): Promise<{ id: string, name: string }[]> => {
    await delay(50);
    const entities = Array.from(allEntities.values());
    return entities
        // Fix: Explicitly type the parameter to resolve type inference issues.
        .filter((e: BankIndex | UpiAppIndex | CreditCardIndex) => e.country === countryCode)
        .map(e => ({ id: e.id, name: e.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
};

// --- New Function for Homepage Search Suggestions ---
export const getSearchSuggestions = async (countryCode: string): Promise<SearchSuggestion[]> => {
    await delay(50);
    const suggestions: SearchSuggestion[] = [];
    const paymentAppPath = countryCode === 'IN' ? '/upi-charges' : '/payment-app-charges';

    // Get Banks
    const banks = await getBankIndex(countryCode);
    banks.forEach(bank => {
        suggestions.push({
            name: bank.name,
            type: 'Bank',
            path: `/bank-charges/${bank.slug}`,
            code: bank.id // Added code for abbreviation search (e.g. 'sbi')
        });
    });

    // Get UPI Apps
    const upiApps = await getUpiAppIndex(countryCode);
    upiApps.forEach(app => {
        suggestions.push({
            name: app.name,
            type: countryCode === 'IN' ? 'UPI' : 'Payment App',
            path: `${paymentAppPath}/${app.slug}`,
            code: app.id // Added code for abbreviation search
        });
    });

    // Get Credit Cards
    const cards = await getCreditCardIndex(countryCode);
    cards.forEach(card => {
        suggestions.push({
            name: card.name,
            type: 'Card',
            path: `/credit-card-charges/${card.slug}`,
            code: card.id // Added code for abbreviation search
        });
    });

    // Get Charges
    const charges = await getCharges(countryCode);
    charges.forEach(charge => {
        suggestions.push({
            name: charge.name,
            type: 'Charge',
            path: `/charges/${charge.id}`,
            details: charge.fullName,
            code: charge.code,
        });
    });

    return suggestions;
};